import { DEFAULT_AUTHN_LOOKBACK_DAYS } from "@p0security/shared/assessment/constants";
import { Node, isConnectedNode } from "@p0security/shared/graph/types";
import { ALL_SCOPE_SENTINEL } from "@p0security/shared/types/assessment";
import {
  AssessmentNodes,
  IdentityNode,
  IdentityType,
  TrustedPrincipals,
} from "@p0security/shared/types/assessment/data/index";
import { Descriptions, Typography } from "@p0security/ui";
import { isa } from "@p0security/util/types/is";
import {
  LastAuthenticated,
  authnHelp,
} from "components/Assessment/components/cells/LastAuthenticated";
import { ScopeContext } from "components/Assessment/contexts/ScopeContext";
import { GraphTooltip } from "components/GraphTooltip";
import { CommandDisplay } from "components/Integrations/CommandDisplay";
import { allGaIntegrations } from "components/Integrations/constants";
import { min } from "lodash";
import pluralize from "pluralize";
import { useContext, useMemo } from "react";

import { ConsumersList } from "../../cells/ConsumersList";
import { CredentialList, identityInfoFromKey } from "../../cells/Credential";
import { GroupAccessTable } from "../../cells/GroupAccess";
import { IdentityExternalLink, IdentityLink } from "../../cells/Identity";
import {
  RiskGroupedPrivilegeList,
  RiskList,
} from "../../cells/PrivilegeAggregate";
import { Resource } from "../../cells/Resource";
import { NodeDescriptions } from "../NodeDescriptions";
import { GroupMembers } from "./GroupMember";
import { Groups } from "./Groups";
import { credentialsByStaleness, credentialsByUsage } from "./grouping";
import {
  credentialsFor,
  identityTypesWithCredentials,
  lastAuthnTimeFor,
} from "./shared";

const { Text } = Typography;
const { Item } = Descriptions;

const hasMembers = (type: IdentityType) => type === "group";
const hasAccessibleBy = (type: IdentityType) =>
  type === "aws-permission-set-role";

export const TrustPolicyDisplay: React.FC<{
  trustedPrincipals: TrustedPrincipals;
}> = ({ trustedPrincipals }) => (
  <div>
    <Typography className="font-bold">Trust Policy:</Typography>
    <CommandDisplay
      hideCopy
      commands={JSON.stringify(
        JSON.parse(trustedPrincipals.trustPolicy),
        null,
        2
      )}
    />
  </div>
);

/* 
We must map each of usedCredentials.used, usedCredentials.unused, staleCredentials.stale, staleCredentials.fresh to
extract only the data part and the additional service account key information. The non-data part (parents, children) may
lead to circular references when exporting to JSON. Also flattens the data so it's easy to answer questions like
"used" and "stale" credentials.
*/
export const credentialExportFromIdentity = (node: IdentityNode) => {
  const credentialMap = new Map<
    string,
    AssessmentNodes["credential"] & { used?: boolean; stale?: boolean }
  >();

  const updateCredential = (
    key: string,
    data: AssessmentNodes["credential"] & {
      used?: boolean;
      stale?: boolean;
    }
  ) => {
    const credential = credentialMap.get(key);
    if (credential) {
      credentialMap.set(key, { ...credential, ...data });
    } else {
      credentialMap.set(key, data);
    }
  };
  const credentials = credentialsFor(node);

  const credentialsUsage = credentialsByUsage(credentials ?? []);
  const credentialsStaleness = credentialsByStaleness(credentials ?? []);

  for (const cred of credentialsUsage.used ?? []) {
    updateCredential(cred.id, { used: true, ...cred });
  }
  for (const cred of credentialsUsage.unused ?? []) {
    updateCredential(cred.id, { used: false, ...cred });
  }
  for (const cred of credentialsStaleness.stale ?? []) {
    updateCredential(cred.id, { stale: true, ...cred });
  }
  for (const cred of credentialsStaleness.fresh ?? []) {
    updateCredential(cred.id, { stale: false, ...cred });
  }

  return Array.from(credentialMap.entries()).map(([key, credential]) => {
    return {
      ...identityInfoFromKey(key, credential.provider),
      ...credential,
    };
  });
};

export const identityLookback = (
  credentials: (AssessmentNodes["credential"] & {
    id: string;
  })[]
) =>
  min(
    credentials.map(
      (c) => c.maxAuthnLookbackDays ?? DEFAULT_AUTHN_LOOKBACK_DAYS
    )
  );

export const IdentityNodeDisplay: React.FC<{ node: IdentityNode }> = ({
  node,
}) => {
  const { aggregates, data, key } = node;
  const { provider, scopeKey } = useContext(ScopeContext);
  const grantCount = aggregates.grants.length;
  const credentials = useMemo(() => credentialsFor(node), [node]);
  const lastAuthnTime = useMemo(
    () => lastAuthnTimeFor(credentials),
    [credentials]
  );
  const lookback = identityLookback(credentials);

  // Only direct consumers, not consumers of identities that _could_ impersonate this identity
  const consumers = useMemo(
    () =>
      node.parents
        .filter(isConnectedNode("credential"))
        .flatMap((c) =>
          c.parents.filter(isConnectedNode("consumer")).map((cc) => cc.data)
        ),
    [node]
  );

  const { access: groupAccess, provider: nodeProvider } = data;

  return (
    <>
      <NodeDescriptions>
        <Item label="Identity">
          <IdentityExternalLink data={data} id={key} />
        </Item>
        <Item label="Provider">
          {nodeProvider ? (
            <Text>
              {allGaIntegrations[nodeProvider as string]?.label ?? nodeProvider}
            </Text>
          ) : (
            <Text type="secondary">(Unknown)</Text>
          )}
        </Item>
        <Item label="Parent">
          {data.parent ? (
            <Resource
              resource={
                {
                  data: {
                    resources: [data.parent],
                    provider: data.provider,
                  },
                  type: "grant",
                } as Node<AssessmentNodes, "grant">
              }
              index={0}
            />
          ) : (
            <Text type="secondary">(Unknown)</Text>
          )}
        </Item>
        <Item label="Last Used">
          <LastAuthenticated
            identity={data}
            last={lastAuthnTime}
            lookback={lookback}
          />
        </Item>
        {
          // consumers do not exist on all-target scope
          provider !== "all" && node.data.type === "service-account" && (
            <Item label="Used By">
              <ConsumersList
                consumers={consumers}
                lastAuthnTime={lastAuthnTime}
              />
            </Item>
          )
        }
        {node.data.type === "user" && (
          <Item label="MFA">
            {node.data.mfa || (
              <GraphTooltip title={authnHelp["user"]}>Unknown</GraphTooltip>
            )}
          </Item>
        )}
        {isa(identityTypesWithCredentials, data.type) &&
          !data.isProviderManaged && (
            <Item label="Authentication Methods">
              <CredentialList
                identityType={data.type}
                credentials={credentials}
              />
            </Item>
          )}
        {hasMembers(data.type) ||
          (hasAccessibleBy(data.type) && (
            <Item
              label={
                hasMembers(data.type)
                  ? "Members"
                  : hasAccessibleBy(data.type)
                    ? "Accessible by"
                    : ""
              }
            >
              <GroupMembers node={node} />
            </Item>
          ))}
        {hasMembers(data.type) && (
          <Item label="Members">
            <GroupMembers node={node} />
          </Item>
        )}
        {(data.type === "user" || data.type === "group") && (
          <Item label="Groups">
            <Typography.Text type="secondary">
              Showing direct parent groups only
            </Typography.Text>
            <Groups node={node} />
          </Item>
        )}
        <Item label="Entitlements">
          <Typography.Paragraph>
            This identity can exercise {grantCount}{" "}
            {pluralize("entitlement", grantCount)}
          </Typography.Paragraph>
          <IdentityLink data={{ ...data, id: key }} show="identity" />
        </Item>
        <Item label="Risks">
          {scopeKey === ALL_SCOPE_SENTINEL ? (
            <RiskList risks={node.aggregates.risks} />
          ) : (
            <RiskGroupedPrivilegeList
              privileges={node.aggregates.privileges}
              provider={provider}
              showControl
            />
          )}
        </Item>
        {groupAccess && (
          <Item label="Access">
            <GroupAccessTable access={groupAccess} />
          </Item>
        )}
      </NodeDescriptions>
      {node.data.trustedPrincipals && (
        <TrustPolicyDisplay
          trustedPrincipals={node.data.trustedPrincipals}
        ></TrustPolicyDisplay>
      )}
    </>
  );
};
